import dayjs from 'dayjs';
import React, { useState } from 'react';
import { SecuritySetupForm } from '../../../components/SecuritySetupForm';
import { Button } from '../../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from '../../../components/ui/dialog';
import { PhrasePersonalInfo } from '../../../utils/security-phrase';

interface IProps {
  /** The user's current security phrase, or null when none is set */
  currentPhrase: string | null;
  /** Whether a security avatar is saved */
  hasAvatar: boolean;
  /** The saved avatar as an image, or null when none is saved or it cannot be shown */
  avatarImage: string | null;
  lastUpdated: string | null;
  /** The signed-in user's name, phone and email, which the phrase must not repeat. */
  personal?: PhrasePersonalInfo;
  onSuccess: () => void;
}

/** The security avatar and security phrase, shown and changed together. */
const SecuritySettings: React.FC<IProps> = ({
  currentPhrase,
  hasAvatar,
  avatarImage,
  lastUpdated,
  personal,
  onSuccess
}) => {
  const [openDialog, setOpenDialog] = useState(false);
  const [saved, setSaved] = useState(false);
  const hasPhrase = !!currentPhrase;
  const hasAny = hasPhrase || hasAvatar;

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSaved(false);
  };

  return (
    <>
      <div className="flex items-center justify-between gap-2 py-4">
        <div className="flex flex-col gap-1">
          <h3 className="text-sm font-medium text-gray-900">
            Security Avatar &amp; Phrase
          </h3>
          <p className="text-sm text-gray-500">
            AyeFace shows your avatar and phrase before you pay. If you
            don&apos;t see them, or they are wrong, do not pay.
          </p>
          <div className="mt-1 flex items-center gap-3">
            {avatarImage ? (
              <img
                src={avatarImage}
                alt="Your security avatar"
                className="h-12 w-12 shrink-0 rounded-xl border border-gray-200 object-cover"
                data-testid="current-security-avatar"
              />
            ) : (
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-dashed border-gray-300 text-center text-[10px] leading-tight text-gray-400">
                No avatar
              </span>
            )}
            <p className="text-sm text-gray-500">
              {hasPhrase ? (
                <>
                  Your security phrase:{' '}
                  <span
                    className="whitespace-nowrap font-medium text-gray-900"
                    data-testid="current-security-phrase"
                  >
                    {currentPhrase}
                  </span>
                </>
              ) : (
                'No security phrase set.'
              )}
            </p>
          </div>
          {lastUpdated && (
            <p className="text-sm italic text-gray-500">
              Security phrase last updated:{' '}
              {dayjs(lastUpdated).format('MMMM DD, YYYY')}
            </p>
          )}
        </div>
        <Button
          type="button"
          className="min-w-24 shrink-0"
          aria-label={
            hasAny
              ? 'Change security avatar and phrase'
              : 'Set security avatar and phrase'
          }
          onClick={() => setOpenDialog(true)}
        >
          {hasAny ? 'Change' : 'Set'}
        </Button>
      </div>

      <Dialog
        open={openDialog}
        onOpenChange={(next) => !next && handleCloseDialog()}
      >
        <DialogContent className="max-h-[calc(100dvh-2rem)] w-[calc(100%-2rem)] max-w-[25rem] overflow-y-auto rounded-3xl focus:outline-none sm:max-w-[26rem]">
          {saved ? (
            <div className="mb-1 mt-3 flex flex-col items-center">
              <DialogTitle className="sr-only">Saved</DialogTitle>
              <DialogDescription className="sr-only">
                AyeFace will now show these before you pay.
              </DialogDescription>
              <img
                src="/assets/images/update-phone-success.png"
                alt="Saved"
                className="mx-auto w-32"
              />
              <h3 className="mt-4 text-center text-sm font-medium">
                Your security avatar and phrase are saved
              </h3>
              <Button
                className="mx-4 mt-6 w-4/5 rounded-full bg-[#5CB891] hover:bg-[#5CB891]"
                onClick={handleCloseDialog}
                type="button"
              >
                Done
              </Button>
            </div>
          ) : (
            <div className="flex flex-col pb-1 pt-3">
              <DialogHeader className="items-center">
                <DialogTitle className="text-center text-xl font-bold text-gray-900">
                  {hasAny ? 'Change avatar & phrase' : 'Set avatar & phrase'}
                </DialogTitle>
                <DialogDescription className="mt-2 text-center text-sm leading-6 text-gray-600">
                  {hasAny
                    ? 'Pick a new avatar, enter a new phrase, or both. Never share them with anyone.'
                    : 'Pick an avatar and choose a phrase. Never share them with anyone.'}
                </DialogDescription>
              </DialogHeader>
              <div className="mt-5">
                <SecuritySetupForm
                  showAvatar
                  showPhrase
                  hasAvatar={hasAvatar}
                  hasPhrase={hasPhrase}
                  personal={personal}
                  phraseInputId="settings-security-phrase"
                  submitLabel="Save"
                  onSaved={() => {
                    setSaved(true);
                    onSuccess();
                  }}
                />
              </div>
              <button
                type="button"
                onClick={handleCloseDialog}
                className="mt-3 self-center rounded-full px-6 py-2 text-sm text-gray-800 hover:text-gray-950"
              >
                Cancel
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SecuritySettings;
